import { useEffect, useRef } from 'react';

import { Box, Button, Heading, Image, Stack, useColorModeValue } from '@ecoinvent/ui';

import { TourContextValue, TourStep } from '../types';

interface WelcomeCardProps {
  steps: TourStep[];
  useTourHook: () => TourContextValue;
  image: string;
}

const WelcomeCard = ({ steps, useTourHook, image }: WelcomeCardProps) => {
  const { state, nextStep, skipTour } = useTourHook();
  const step = steps[0];
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.200');
  const isOpen = state.isActive && state.currentStep === 0 && step.type === 'card';

  const cardRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const headingId = 'welcome-card-heading';
  const contentId = 'welcome-card-content';

  // Focus management
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      cardRef.current?.focus();
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      ref={cardRef}
      role="dialog"
      aria-labelledby={headingId}
      aria-describedby={contentId}
      aria-modal="true"
      tabIndex={-1}
      position="fixed"
      right="24px"
      bottom="24px"
      zIndex="popover"
      bg={bgColor}
      borderRadius="lg"
      maxW="320px"
      boxShadow="xl"
      borderWidth="0.5px"
      borderColor={borderColor}
      p={6}
      _focus={{ outline: 'none' }}
    >
      <Image src={image} alt="Dataset overview" w="100%" borderRadius="md" />
      <Stack spacing={4} pt={6}>
        <Heading id={headingId} size="md">
          {step.title}
        </Heading>
        <Box id={contentId} fontSize="sm">
          {step.content}
        </Box>
        <Stack spacing={2}>
          <Button colorScheme="green" onClick={nextStep}>
            {step.buttonText}
          </Button>
          <Button variant="ghost" colorScheme="gray" size="sm" onClick={skipTour}>
            Skip tour
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default WelcomeCard;
